/**
 * Document Preview Generator
 * 
 * Generates a preview document by applying operations to a copy.
 * Used for side-by-side diff view before accepting AI changes.
 */

import type { AeroDocument, DocumentSection, ContentBlock } from '../types/document-schema';
import type { 
  DocumentOperation, 
  InsertBlockOp, 
  InsertSectionOp,
  UpdateTextOp,
  ReplaceBlockOp,
  DeleteBlockOp,
  UpdateSectionTitleOp,
  DeleteSectionOp,
  UpdateTableCellOp,
  MoveSectionOp
} from '../types/document-operations';
import type { TableRow } from '../types/document-schema';
import { logger } from './logger';

/**
 * Generate preview document with operations applied
 */
export function generatePreviewDocument(
  original: AeroDocument,
  operations: DocumentOperation[]
): AeroDocument {
  logger.debug('[PreviewGen] ========== GENERATING PREVIEW ==========');
  logger.debug('[PreviewGen] Original document:', {
    documentNumber: original.metadata.documentNumber,
    sectionCount: original.body.length,
    totalBlocks: original.body.reduce((sum, s) => sum + s.blocks.length, 0)
  });
  logger.debug('[PreviewGen] Operations to apply:', operations.length);
  operations.forEach((op, i) => {
    logger.debug(`[PreviewGen]   ${i + 1}. ${op.op}`, {
      targetId: op.targetId,
      sectionId: 'sectionId' in op ? op.sectionId : undefined,
      hasNewBlock: 'newBlock' in op ? !!(op as InsertBlockOp).newBlock : false,
      hasNewSection: 'newSection' in op ? !!(op as InsertSectionOp).newSection : false
    });
  });
  
  // Deep clone to avoid mutating original
  const preview: AeroDocument = JSON.parse(JSON.stringify(original));
  
  // Apply each operation
  for (let i = 0; i < operations.length; i++) {
    logger.debug(`[PreviewGen] Applying operation ${i + 1}/${operations.length}`);
    applyOperationToPreview(preview, operations[i]);
  }
  
  logger.debug('[PreviewGen] After applying operations:', {
    sectionCount: preview.body.length,
    totalBlocks: preview.body.reduce((sum, s) => sum + s.blocks.length, 0)
  });
  
  // Recompute section numbering using same logic as backend
  preview.body = computePreviewSectionNumbering(preview.body);
  
  // Update metadata
  preview.version = new Date().toISOString();
  preview.lastModified = preview.version;
  preview.modifiedBy = 'AI Assistant (preview)';
  
  logger.debug('[PreviewGen] ========== PREVIEW COMPLETE ==========');
  
  return preview;
}

/**
 * Apply a single operation to preview document
 */
function applyOperationToPreview(doc: AeroDocument, op: DocumentOperation): void {
  logger.debug('[PreviewGen] Applying operation:', op.op, 'targetId:', op.targetId || op.sectionId);
  
  switch (op.op) {
    case 'update_text':
      applyUpdateText(doc, op);
      break;
    
    case 'replace_block':
      applyReplaceBlock(doc, op);
      break;
    
    case 'insert_block_after':
    case 'insert_block_before':
      applyInsertBlock(doc, op);
      break;
    
    case 'delete_block':
      applyDeleteBlock(doc, op);
      break;
    
    case 'update_section_title':
      applyUpdateSectionTitle(doc, op);
      break;
    
    case 'update_table_cell':
      applyUpdateTableCell(doc, op);
      break;
    
    case 'insert_section_after':
    case 'insert_section_before':
      applyInsertSection(doc, op);
      break;
    
    case 'delete_section':
      applyDeleteSection(doc, op);
      break;
    
    case 'move_section':
      applyMoveSection(doc, op);
      break;
    
    default:
      logger.warn('[PreviewGen] Unknown operation:', op);
  }
}

/**
 * Apply update_text operation
 */
function applyUpdateText(doc: AeroDocument, op: UpdateTextOp): void {
  logger.debug('[PreviewGen] update_text - Looking for block:', op.targetId);
  const block = findBlock(doc, op.targetId);
  
  if (!block) {
    logger.error('[PreviewGen] ❌ BLOCK NOT FOUND:', op.targetId);
    const allBlockIds = doc.body.flatMap(s => s.blocks.map(b => b.id));
    logger.error('[PreviewGen] Available block IDs:', allBlockIds);
    return;
  }
  
  logger.debug('[PreviewGen] ✅ Block found, type:', block.type);
  
  if (block.type === 'paragraph' || block.type === 'heading') {
    logger.debug('[PreviewGen] Updating text from:', block.content.text?.substring(0, 50), '...');
    logger.debug('[PreviewGen] Updating text to:', op.newText?.substring(0, 50), '...');
    block.content.text = op.newText;
    logger.debug('[PreviewGen] ✅ Text updated');
  } else {
    logger.error('[PreviewGen] ❌ Cannot update text - block type is:', block.type);
  }
}

/**
 * Apply replace_block operation
 */
function applyReplaceBlock(doc: AeroDocument, op: ReplaceBlockOp): void {
  const section = findSectionContainingBlock(doc, op.targetId);
  if (!section) return;
  
  const blockIndex = section.blocks.findIndex(b => b.id === op.targetId);
  if (blockIndex >= 0) {
    // CRITICAL FIX: Preserve original ID if new block doesn't have one
    const blockToReplace = { ...op.newBlock };
    if (!blockToReplace.id) {
      blockToReplace.id = op.targetId; // Reuse the target block's ID
      logger.warn('[PreviewGen] ⚠️ Replacement block missing ID, using targetId:', blockToReplace.id);
    }
    section.blocks[blockIndex] = blockToReplace;
  }
}

/**
 * Apply insert_block operation
 */
function applyInsertBlock(doc: AeroDocument, op: InsertBlockOp): void {
  logger.debug('[PreviewGen] insert_block - Looking for section:', op.sectionId);
  const section = doc.body.find(s => s.id === op.sectionId);
  
  if (!section) {
    logger.error('[PreviewGen] ❌ SECTION NOT FOUND:', op.sectionId);
    logger.error('[PreviewGen] Available sections:', doc.body.map(s => s.id));
    return;
  }
  
  logger.debug('[PreviewGen] ✅ Section found:', section.title, 'blocks:', section.blocks.length);
  logger.debug('[PreviewGen] Looking for target block:', op.targetId);
  
  const targetIndex = section.blocks.findIndex(b => b.id === op.targetId);
  
  if (targetIndex === -1) {
    logger.error('[PreviewGen] ❌ TARGET BLOCK NOT FOUND:', op.targetId);
    logger.error('[PreviewGen] Available blocks in section:', section.blocks.map(b => b.id));
    logger.error('[PreviewGen] newBlock to insert:', op.newBlock);
    return;  // FAIL LOUDLY - don't fallback
  }
  
  // CRITICAL FIX: Generate ID if block doesn't have one (AI sometimes forgets)
  const blockToInsert = { ...op.newBlock };
  if (!blockToInsert.id) {
    // Generate a unique ID based on section and position
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 7);
    blockToInsert.id = `${section.id.replace('sec-', 'p-')}-${timestamp}-${random}`;
    logger.warn('[PreviewGen] ⚠️ Block missing ID, generated:', blockToInsert.id);
  }
  
  const insertIndex = op.op === 'insert_block_after' ? targetIndex + 1 : targetIndex;
  logger.debug('[PreviewGen] ✅ Inserting at index:', insertIndex, 'type:', blockToInsert.type, 'id:', blockToInsert.id);
  section.blocks.splice(insertIndex, 0, blockToInsert);
  logger.debug('[PreviewGen] ✅ Insert complete, section now has', section.blocks.length, 'blocks');
}

/**
 * Apply delete_block operation
 */
function applyDeleteBlock(doc: AeroDocument, op: DeleteBlockOp): void {
  logger.debug('[PreviewGen] delete_block - Section:', op.sectionId, 'Block:', op.targetId);
  const section = doc.body.find(s => s.id === op.sectionId);
  
  if (!section) {
    logger.error('[PreviewGen] ❌ SECTION NOT FOUND:', op.sectionId);
    logger.error('[PreviewGen] Available sections:', doc.body.map(s => s.id));
    return;
  }
  
  logger.debug('[PreviewGen] ✅ Section found, blocks:', section.blocks.length);
  const blockIndex = section.blocks.findIndex(b => b.id === op.targetId);
  
  if (blockIndex === -1) {
    logger.error('[PreviewGen] ❌ BLOCK NOT FOUND:', op.targetId);
    logger.error('[PreviewGen] Available blocks:', section.blocks.map(b => b.id));
    return;
  }
  
  logger.debug('[PreviewGen] ✅ Deleting block at index:', blockIndex);
  section.blocks.splice(blockIndex, 1);
  logger.debug('[PreviewGen] ✅ Delete complete, section now has', section.blocks.length, 'blocks');
}

/**
 * Apply update_section_title operation
 */
function applyUpdateSectionTitle(doc: AeroDocument, op: UpdateSectionTitleOp): void {
  const section = doc.body.find(s => s.id === op.targetId);
  if (section) {
    section.title = op.newTitle;
  }
}

/**
 * Apply update_table_cell operation
 */
function applyUpdateTableCell(doc: AeroDocument, op: UpdateTableCellOp): void {
  const table = findBlock(doc, op.tableId);
  if (!table || table.type !== 'table') return;
  
  const row = table.content.rows?.find((r: TableRow) => r.id === op.rowId);
  if (!row) return;
  
  if (row.cells && row.cells[op.columnId]) {
    row.cells[op.columnId].value = op.newValue;
  }
}

/**
 * Apply insert_section operation
 */
function applyInsertSection(doc: AeroDocument, op: InsertSectionOp): void {
  const targetIndex = doc.body.findIndex(s => s.id === op.targetId);
  if (targetIndex >= 0) {
    const insertIndex = op.op === 'insert_section_after' ? targetIndex + 1 : targetIndex;
    doc.body.splice(insertIndex, 0, op.newSection);
  }
}

/**
 * Apply delete_section operation
 */
function applyDeleteSection(doc: AeroDocument, op: DeleteSectionOp): void {
  const sectionIndex = doc.body.findIndex(s => s.id === op.targetId);
  if (sectionIndex >= 0) {
    logger.debug('[PreviewGen] Deleting section:', op.targetId);
    doc.body.splice(sectionIndex, 1);
  } else {
    logger.warn('[PreviewGen] Section not found for deletion:', op.targetId);
  }
}

/**
 * Apply move_section operation
 */
function applyMoveSection(doc: AeroDocument, op: MoveSectionOp): void {
  const sectionIndex = doc.body.findIndex(s => s.id === op.sectionId);
  if (sectionIndex < 0) {
    logger.warn('[PreviewGen] Section not found for move:', op.sectionId);
    return;
  }
  
  // Remove section from current position
  const [section] = doc.body.splice(sectionIndex, 1);
  
  // Update parent relationship
  section.parent = op.newParentId || null;
  
  // Insert at new position
  // Note: This is simplified - full implementation would handle hierarchical moves
  if (op.newIndex >= 0 && op.newIndex <= doc.body.length) {
    doc.body.splice(op.newIndex, 0, section);
  } else {
    // Invalid index - put at end
    doc.body.push(section);
  }
  
  logger.debug('[PreviewGen] Moved section:', op.sectionId, 'to index:', op.newIndex);
}

/**
 * Find a block by ID across all sections
 */
function findBlock(doc: AeroDocument, blockId: string): ContentBlock | null {
  for (const section of doc.body) {
    const block = section.blocks.find(b => b.id === blockId);
    if (block) return block;
  }
  return null;
}

/**
 * Find the section containing a specific block
 */
function findSectionContainingBlock(doc: AeroDocument, blockId: string): DocumentSection | null {
  return doc.body.find(s => s.blocks.some(b => b.id === blockId)) || null;
}

/**
 * Compute section numbering (frontend version matching backend logic)
 */
function computePreviewSectionNumbering(sections: DocumentSection[]): DocumentSection[] {
  // Only number top-level sections (those without parents)
  const topLevelSections = sections.filter(s => !s.parent);
  
  let sectionCounter = 0;
  let appendixCounter = 0;
  
  for (const section of topLevelSections) {
    if (section.type === 'appendix') {
      appendixCounter++;
      section.number = String.fromCharCode(64 + appendixCounter); // A, B, C
    } else {
      sectionCounter++;
      section.number = sectionCounter.toString();
    }
    
    // Recursively number children
    if (section.children && section.children.length > 0) {
      const childSections = section.children
        .map(childId => sections.find(s => s.id === childId))
        .filter(Boolean) as DocumentSection[];
      
      numberSubsections(childSections, section.number, sections);
    }
  }
  
  return sections;
}

function numberSubsections(children: DocumentSection[], parentNumber: string, allSections: DocumentSection[]): void {
  let counter = 0;
  
  for (const section of children) {
    counter++;
    section.number = `${parentNumber}.${counter}`;
    
    if (section.children && section.children.length > 0) {
      const childSections = section.children
        .map(childId => allSections.find(s => s.id === childId))
        .filter(Boolean) as DocumentSection[];
      
      numberSubsections(childSections, section.number, allSections);
    }
  }
}
